import StarterKit from '@tiptap/starter-kit';
import { TableKit, TableView } from '@tiptap/extension-table';
import Strike from '@tiptap/extension-strike';
import CodeExtension from '@tiptap/extension-code';
import TiptapHighlight from '@tiptap/extension-highlight';
import TiptapLink from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontSize } from '@tiptap/extension-text-style/font-size';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import Image from '@tiptap/extension-image';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { NodeRange } from '@tiptap/extension-node-range';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { CharacterCount, Placeholder } from '@tiptap/extensions';
import { DiffBlockAttributes, DiffDelete, DiffInsert } from '../../extensions/DiffMarks';
import MermaidBlock from '../../extensions/MermaidBlock';
import PageBreak from '../../extensions/PageBreak';
import Pagination from '../../extensions/Pagination';
import { TrustMetadataAttributes, TrustSection } from '../../extensions/TrustSection';
import BlockId from '../../extensions/BlockId';
import EditorRuntime from '../../extensions/EditorRuntime';
import SearchReplace from '../../extensions/SearchReplace';
import SlashCommand from '../../extensions/SlashCommand';
import {
  GeneratedField,
  GeneratedFieldMark,
  TemplateAttributes,
  TemplateField,
  TemplateFieldBlock,
  TemplateVariableHighlight,
} from '../../extensions/TemplateField';
import { buildSlashItems } from './slashItems';

/**
 * Review and addressing attributes that node views must mirror on their outer DOM element.
 * Node views built by TipTap/prosemirror-tables copy node attributes only once (or never), and
 * the review engine changes these through setNodeMarkup, so they are synced on every update.
 * `data-change-id` is only meaningful while a node carries a review state.
 * @param {import('@tiptap/pm/model').Node} node - Node.
 * @returns {Array<[string, string|null]>} Attribute name/value pairs (null removes).
 */
const reviewDomAttributes = (node) => {
  const diffBlock = node.attrs.diffBlock === 'insert' || node.attrs.diffBlock === 'delete' ? node.attrs.diffBlock : null;
  return [
    ['data-diff-block', diffBlock],
    ['data-change-id', diffBlock && node.attrs.diffChangeId ? String(node.attrs.diffChangeId) : null],
    ['data-block-id', node.attrs.blockId ? String(node.attrs.blockId) : null],
  ];
};

/**
 * Write the review/addressing attributes of `node` onto `element`.
 * @param {HTMLElement|null|undefined} element - Target element.
 * @param {import('@tiptap/pm/model').Node} node - Node.
 * @returns {void}
 */
const syncReviewDomAttributes = (element, node) => {
  if (!element) return;
  reviewDomAttributes(node).forEach(([name, value]) => {
    if (value) {
      if (element.getAttribute(name) !== value) element.setAttribute(name, value);
    } else if (element.hasAttribute(name)) {
      element.removeAttribute(name);
    }
  });
};

/** Image attributes the resizable node view applies in place (everything else rebuilds the view). */
const IN_PLACE_IMAGE_ATTRS = new Set(['width', 'height', 'diffBlock', 'diffChangeId', 'blockId']);

/**
 * Whether two attribute objects differ in any attribute that cannot be applied in place.
 * @param {object} a - Previous attributes.
 * @param {object} b - Next attributes.
 * @returns {boolean} True when the image node view must be rebuilt.
 */
const imageNeedsRebuild = (a, b) => {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const key of keys) {
    if (!IN_PLACE_IMAGE_ATTRS.has(key) && a[key] !== b[key]) return true;
  }
  return false;
};

/** Text wrap values of an image (`data-wrap`): 'none' keeps the image on its own line. */
export const IMAGE_WRAPS = ['none', 'left', 'right'];

/**
 * Normalize a wrap value. Anything unknown, and every saved image without the attribute, is 'none'.
 * @param {unknown} value - Raw value.
 * @returns {'none'|'left'|'right'} Wrap.
 */
export const normalizeImageWrap = (value) => {
  const wrap = String(value || '').trim().toLowerCase();
  return wrap === 'left' || wrap === 'right' ? wrap : 'none';
};

/**
 * Normalize an alignment value (centre, like saved images without data-align).
 * @param {unknown} value - Raw value.
 * @returns {'left'|'center'|'right'} Alignment.
 */
export const normalizeImageAlign = (value) => {
  const align = String(value || '').trim().toLowerCase();
  return align === 'left' || align === 'right' ? align : 'center';
};

/**
 * Write the placement attributes (`data-align`, `data-wrap`) onto the node view's outer
 * container. The resizable view wraps the <img> in a flex container, so margins on the <img>
 * cannot place it: the editor CSS aligns or floats the container (with its resize handles).
 * Placement changes rebuild the view, so setting them once per view is enough.
 * @param {HTMLElement|null|undefined} element - Container.
 * @param {import('@tiptap/pm/model').Node} node - Image node.
 * @returns {void}
 */
const syncImagePlacementDom = (element, node) => {
  if (!element) return;
  element.setAttribute('data-align', normalizeImageAlign(node.attrs.align));
  const wrap = normalizeImageWrap(node.attrs.wrap);
  if (wrap === 'none') element.removeAttribute('data-wrap');
  else element.setAttribute('data-wrap', wrap);
};

/**
 * Block image with `data-image-id`, `data-align`, `data-wrap`, and TipTap's built-in resize.
 *
 * `wrap` ('none' | 'left' | 'right', saved as `data-wrap`) floats the image so text flows
 * beside it. 'none' is not written to the HTML, so images saved before wrapping existed and
 * unwrapped images keep exactly the HTML they always had.
 *
 * TipTap's resizable image view copies HTML attributes onto the <img> only when it is created
 * and keeps the old DOM for every later attribute change. This wrapper keeps the DOM honest:
 * review state (`data-diff-block` / `data-change-id`) and `data-block-id` live on the view's
 * outer container and follow every update (so a pending deletion shows in red, a resolved one
 * returns to normal, and the review chip can find the change); width/height follow undo/redo;
 * any other attribute change (src, alt, title, align, wrap, image id) rebuilds the view from
 * renderHTML.
 */
export const DocumentImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-image-id': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-image-id'),
        renderHTML: (attributes) => (attributes['data-image-id'] ? { 'data-image-id': attributes['data-image-id'] } : {}),
      },
      align: {
        default: 'center',
        parseHTML: (element) => element.getAttribute('data-align') || 'center',
        renderHTML: (attributes) => ({ 'data-align': attributes.align || 'center' }),
      },
      wrap: {
        default: 'none',
        parseHTML: (element) => normalizeImageWrap(element.getAttribute('data-wrap')),
        renderHTML: (attributes) => {
          const wrap = normalizeImageWrap(attributes.wrap);
          return wrap === 'none' ? {} : { 'data-wrap': wrap };
        },
      },
    };
  },

  addNodeView() {
    const createParentView = this.parent?.();
    if (typeof createParentView !== 'function') return null;
    return (props) => {
      const view = createParentView(props);
      if (!view || !view.dom) return view;
      const img = view.element || view.dom.querySelector('img');
      // Review state is drawn on the container only (styling the <img> as well would stack the
      // opacity and hide the tint behind the picture).
      if (img) ['data-diff-block', 'data-change-id', 'data-block-id'].forEach((name) => img.removeAttribute(name));
      syncReviewDomAttributes(view.dom, props.node);
      syncImagePlacementDom(view.dom, props.node);

      const baseUpdate = typeof view.update === 'function' ? view.update.bind(view) : null;
      view.update = (node, decorations, innerDecorations) => {
        const previous = view.node || props.node;
        if (node.type !== previous.type || imageNeedsRebuild(previous.attrs, node.attrs)) return false;
        if (baseUpdate && !baseUpdate(node, decorations, innerDecorations)) return false;
        view.node = node;
        syncReviewDomAttributes(view.dom, node);
        if (img && !view.isResizing) {
          if (previous.attrs.width !== node.attrs.width) img.style.width = node.attrs.width ? `${node.attrs.width}px` : '';
          if (previous.attrs.height !== node.attrs.height) img.style.height = node.attrs.height ? `${node.attrs.height}px` : '';
        }
        return true;
      };
      return view;
    };
  },
});

/**
 * Column-resizing table view that mirrors review state and the block id on its wrapper.
 * prosemirror-tables renders tables through this view while the editor is editable, and the
 * stock TableView never puts node attributes on the DOM.
 */
export class ReviewTableView extends TableView {
  /**
   * @param {import('@tiptap/pm/model').Node} node - Table node.
   * @param {number} cellMinWidth - Minimum cell width.
   * @param {import('@tiptap/pm/view').EditorView} [view] - Editor view.
   */
  constructor(node, cellMinWidth, view) {
    super(node, cellMinWidth, view);
    syncReviewDomAttributes(this.dom, node);
  }

  /**
   * @param {import('@tiptap/pm/model').Node} node - Next node.
   * @returns {boolean} Whether the view was updated in place.
   */
  update(node) {
    const ok = super.update(node);
    if (ok) syncReviewDomAttributes(this.dom, node);
    return ok;
  }
}

/**
 * Placeholder text per node, reading the host placeholder from runtime storage.
 * @param {{editor: import('@tiptap/core').Editor, node: import('@tiptap/pm/model').Node}} props - Placeholder props.
 * @returns {string} Placeholder.
 */
const placeholderText = ({ editor, node }) => {
  if (node.type.name === 'heading') return `Heading ${node.attrs.level}`;
  const runtime = editor.storage.quillRuntime || {};
  if (editor.isEmpty && runtime.placeholder) return runtime.placeholder;
  return runtime.mode === 'template'
    ? "Type '/' for commands or an AI field"
    : "Type '/' for commands or Ask AI";
};

/**
 * Build the editor's extension list. Called once per editor (memoized by the host), so
 * TipTap never re-runs setOptions on re-render. Everything prop-dependent is read from
 * `editor.storage.quillRuntime` at use time.
 * @param {{enableImages?: boolean}} [options] - Options fixed for the editor's lifetime.
 * @returns {Array<import('@tiptap/core').AnyExtension>} Extensions.
 */
export function buildEditorExtensions({ enableImages = true } = {}) {
  return [
    StarterKit.configure({
      strike: false,
      code: false,
      link: false,
      underline: false,
    }),
    Strike,
    CodeExtension,
    Underline,
    Subscript,
    Superscript,
    TextStyle,
    Color,
    FontFamily.configure({ types: ['textStyle'] }),
    FontSize.configure({ types: ['textStyle'] }),
    TiptapHighlight.configure({ multicolor: true }),
    TiptapLink.configure({
      openOnClick: false,
      autolink: true,
      HTMLAttributes: { class: 'tiptap-link', rel: 'noopener noreferrer nofollow' },
    }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    TableKit.configure({ table: { resizable: true, View: ReviewTableView } }),
    TaskList,
    TaskItem.configure({ nested: true }),
    NodeRange,
    Placeholder.configure({ placeholder: placeholderText, includeChildren: false }),
    CharacterCount,
    EditorRuntime,
    BlockId,
    DiffDelete,
    DiffInsert,
    DiffBlockAttributes,
    TrustMetadataAttributes,
    TrustSection,
    MermaidBlock,
    PageBreak,
    Pagination,
    TemplateField,
    TemplateFieldBlock,
    TemplateAttributes,
    TemplateVariableHighlight,
    GeneratedField,
    GeneratedFieldMark,
    SearchReplace,
    SlashCommand.configure({ getItems: (editor) => buildSlashItems(editor) }),
    ...(enableImages
      ? [
        DocumentImage.configure({
          inline: false,
          allowBase64: true,
          resize: {
            enabled: true,
            minWidth: 50,
            minHeight: 50,
            alwaysPreserveAspectRatio: true,
          },
          HTMLAttributes: { class: 'tiptap-image' },
        }),
      ]
      : []),
  ];
}
